import { ReactElement, useCallback, useMemo } from 'react';
import Breadcrumbs, { BreadcrumbItem } from 'shell/components/shared/breadcrumb';
import { useEventCallback } from '@mui/material';
import {
  createSearchParams,
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import SipoPersonForm from 'shell/components/shared/sipo-person-form';
import PmiTitle from 'shell/components/shared/title';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { SubjectData } from 'shell/models/person';
import { RegistrationCivilUnionNonResidentCrumbs } from 'mutation/constants/registration-civil-union-non-resident-crumbs';

interface EditPersonsPageCivilUnionNonResidentProps {
  goBackUrl?: string;
  redirectUrl?: string;
  breadcrumbItems?: BreadcrumbItem[];
}
const EditPersonsPageCivilUnionNonResidentPage = ({
  breadcrumbItems = RegistrationCivilUnionNonResidentCrumbs,
  redirectUrl = '/mutazioni/persona/unione-civile/non-residenti/registrazione/:idIntestatario/:idSchedaSoggettoANPR',
  goBackUrl = '/mutazioni/persona/unione-civile/non-residenti/registrazione/:idIntestatario/:idSchedaSoggettoANPR',
}: EditPersonsPageCivilUnionNonResidentProps): ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const stateRequestApplicant = useLocation().state;

  const params = useParams();

  const idIntestatarioMatrimonio = params.idIntestatario;
  const idSchedaSoggettoANPRMatrimonio = params.idSchedaSoggettoANPR;

  // id intestatrio se presente fare get perchè siamo in modifica
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';
  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const isEdit = useMemo(() => {
    if ((idIntestatario && idIntestatario !== '0') || (idAnpr && idAnpr !== '0')) {
      return true;
    }

    return false;
  }, [idIntestatario, idAnpr]);

  const handleOnSuccess = useEventCallback(
    (newIdIntestatario?: string, state?: { subjectData?: SubjectData }) => {
      if (isEdit) {
        navigate(
          {
            pathname: generatePath(redirectUrl, {
              idIntestatario: idIntestatarioMatrimonio || '0',
              idSchedaSoggettoANPR: idSchedaSoggettoANPRMatrimonio || '0',
            }),
            search: `?${createSearchParams({
              idIntestatario: newIdIntestatario || idIntestatario || '',
              idAnpr: idAnpr || '',
              codiceFiscale: fiscalCode,
            })}`,
          },
          {
            state: stateRequestApplicant,
          }
        );

        return;
      }

      navigate(
        {
          pathname: generatePath(redirectUrl, {
            idIntestatario: idIntestatarioMatrimonio || '0',
            idSchedaSoggettoANPR: idSchedaSoggettoANPRMatrimonio || '0',
          }),
          search: `?${createSearchParams({
            codiceFiscale: fiscalCode,
            idIntestatario: newIdIntestatario || '',
            idSchedaSoggettoANPR: idAnpr || '',
          })}`,
        },
        {
          state: {
            ...stateRequestApplicant,
            ...state,
          },
        }
      );
    }
  );

  const handleExit = useCallback(() => {
    navigate({
      pathname: generatePath(goBackUrl, {
        idIntestatario: idIntestatarioMatrimonio || '0',
        idSchedaSoggettoANPR: idSchedaSoggettoANPRMatrimonio || '0',
      }),
      search: `?${createSearchParams({
        codiceFiscale: fiscalCode,
        idIntestatario,
        idSchedaSoggettoANPR: idAnpr || '',
      })}`,
    });
  }, [fiscalCode, idAnpr]);

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbItems,
          {
            name: isEdit ? 'Modifica persona' : 'Nuova persona',
          },
        ]}
      />
      <PmiTitle title={isEdit ? 'Modifica persona' : 'Nuova persona'} />
      <SipoPersonForm
        onExit={handleExit}
        onSuccess={handleOnSuccess}
        urlGetData={
          idIntestatario && idIntestatario !== '0'
            ? `${__PERSON_API_URL__}/non-residenti/sipo/soggetto/${idIntestatario}`
            : `${__PERSON_NEW_API_URL__}/persona`
        }
        fiscalCode={fiscalCode}
        urlRegistration={`${__PERSON_API_URL__}/non-residenti/registrazione`}
        urlUpdate={`${__PERSON_API_URL__}/non-residenti/modifica`}
        skipBirthActPartCheck
      />
    </>
  );
};

EditPersonsPageCivilUnionNonResidentPage.displayName = 'Person Sipo Edit Page component';

export default EditPersonsPageCivilUnionNonResidentPage;
